/**
 * LeadRows — neue Treffer (auswählbar, Agent je Zeile) und bereits vorhandene Treffer
 * (nicht zuweisbar). Zeilen ~56px wie die sevdesk-Liste, Badges als Pastell-Pill mit Punkt.
 */
import { Globe, Phone } from 'lucide-react';
import { BADGE } from '@/components/ausgaben/dna';
import { cn } from '@/lib/utils';
import { agentLabel, openCount } from './AssignmentRules';

const PRIORITY_BADGE = {
  'Hohe Priorität': BADGE.due,
  'Bestandskunden-Nähe': BADGE.open,
  'Neue Branche': BADGE.draft,
  Standard: BADGE.neutral,
};

function Pill({ className, children }) {
  return (
    <span className={cn('inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[12px] font-semibold', className)}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />{children}
    </span>
  );
}

const address = (l) => [l.strasse, [l.plz, l.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ');

export function LeadRow({ lead, checked, onToggle, agent, onAgent, agents, stats }) {
  const pid = lead.google_place_id;
  return (
    <li className={cn('grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 px-4 py-3 sm:grid-cols-[auto_minmax(0,1fr)_14rem] sm:items-center', checked && 'bg-dna-warm/60')}>
      <input type="checkbox" checked={checked} onChange={() => onToggle(pid)} aria-label={`${lead.name} auswählen`}
        className="mt-0.5 h-5 w-5 accent-dna-primary sm:mt-0" />
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-[15px] font-semibold text-dna-primary">{lead.name}</p>
          <Pill className={PRIORITY_BADGE[lead.priority_category] || BADGE.neutral}>{lead.lead_score}</Pill>
        </div>
        <p className="truncate text-[13px] text-dna-muted">{lead.branche} · {address(lead) || 'Adresse folgt'}</p>
        {(lead.telefon || lead.website) && (
          <p className="mt-0.5 flex min-w-0 flex-wrap gap-x-3 text-[13px] text-dna-text">
            {lead.telefon && <span className="inline-flex items-center gap-1 tabular-nums"><Phone className="h-3.5 w-3.5 text-dna-placeholder" />{lead.telefon}</span>}
            {lead.website && <span className="inline-flex min-w-0 items-center gap-1"><Globe className="h-3.5 w-3.5 shrink-0 text-dna-placeholder" /><span className="truncate">{lead.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</span></span>}
          </p>
        )}
      </div>
      <select aria-label={`Agent für ${lead.name}`} value={agent || ''} onChange={(e) => onAgent(pid, e.target.value)}
        className={cn('col-start-2 h-10 w-full min-w-0 rounded-md border px-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-dna-focus sm:col-start-3',
          agent ? 'border-dna-field bg-white text-dna-text' : 'border-dna-open-fg/40 bg-dna-open-bg font-semibold text-dna-open-fg')}>
        <option value="">Ohne Agent</option>
        {agents.map((a) => <option key={a.email} value={a.email}>{agentLabel(a)} · {openCount(stats, a.email)} offen</option>)}
      </select>
    </li>
  );
}

const REASON = { task: { label: 'Offene Aufgabe', cls: BADGE.due }, lead: { label: 'Kontakt', cls: BADGE.neutral } };

export function BlockedRow({ lead, agents }) {
  const { conflict } = lead;
  const owner = conflict.agent
    ? agentLabel(agents.find((a) => a.email?.toLowerCase() === String(conflict.agent).toLowerCase()) || { email: conflict.agent })
    : 'kein Agent';
  const reason = REASON[conflict.reason];
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-semibold text-dna-text">{lead.name}</p>
        <p className="truncate text-[13px] text-dna-muted">{address(lead)}</p>
        <p className="mt-0.5 text-[13px] text-dna-text">
          bei <span className="font-semibold">{owner}</span>
          {conflict.taskTypes.length > 0 && <span className="text-dna-muted"> · {conflict.taskTypes.join(', ')}</span>}
        </p>
      </div>
      <Pill className={reason.cls}>{reason.label}</Pill>
    </li>
  );
}

export function SkeletonRows({ n = 5 }) {
  return Array.from({ length: n }, (_, i) => (
    <li key={i} className="flex items-center gap-3 px-4 py-4" aria-hidden>
      <span className="h-5 w-5 rounded bg-dna-tab" />
      <span className="flex-1 space-y-2">
        <span className="block h-3.5 w-2/5 animate-pulse rounded bg-dna-tab" />
        <span className="block h-3 w-3/5 animate-pulse rounded bg-dna-warm" />
      </span>
      <span className="hidden h-10 w-56 rounded-md bg-dna-warm sm:block" />
    </li>
  ));
}
